import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';

/**
 * Embedding models whose output dimensionality matches the `vector` column
 * width (EMBEDDING_DIM = 1536) in the database. Adding a model with a
 * different dimensionality requires a migration + re-embed, so the set is
 * constrained here to prevent KBs that can never ingest.
 */
export const embeddingModels = [
  'openai:text-embedding-3-small',
  'openai:text-embedding-ada-002',
] as const;
export const embeddingModelSchema = z.enum(embeddingModels);
export type EmbeddingModelId = z.infer<typeof embeddingModelSchema>;

export const knowledgeBaseSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).default(''),
    embeddingModel: embeddingModelSchema.default('openai:text-embedding-3-small'),
    chunkSize: z.number().int().min(128).max(4000).default(1000),
    chunkOverlap: z.number().int().min(0).max(1000).default(150),
  })
  .merge(timestampsSchema);
export type KnowledgeBase = z.infer<typeof knowledgeBaseSchema>;

export const createKnowledgeBaseSchema = knowledgeBaseSchema.pick({
  name: true,
  description: true,
  embeddingModel: true,
  chunkSize: true,
  chunkOverlap: true,
});
export type CreateKnowledgeBase = z.infer<typeof createKnowledgeBaseSchema>;

export const documentStatuses = ['pending', 'processing', 'ready', 'failed'] as const;
export const documentStatusSchema = z.enum(documentStatuses);
export type DocumentStatus = z.infer<typeof documentStatusSchema>;

export const documentSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    knowledgeBaseId: idSchema,
    title: z.string().min(1).max(500),
    sourceUri: z.string().optional(),
    mimeType: z.string().default('text/plain'),
    status: documentStatusSchema.default('pending'),
    chunkCount: z.number().int().nonnegative().default(0),
  })
  .merge(timestampsSchema);
export type Document = z.infer<typeof documentSchema>;

export const ingestDocumentSchema = z.object({
  title: z.string().min(1).max(500),
  content: z.string().min(1),
  sourceUri: z.string().optional(),
  mimeType: z.string().default('text/plain'),
});
export type IngestDocument = z.infer<typeof ingestDocumentSchema>;

export const retrievalQuerySchema = z.object({
  query: z.string().min(1),
  topK: z.number().int().min(1).max(50).default(6),
  knowledgeBaseIds: z.array(idSchema).optional(),
});
export type RetrievalQuery = z.infer<typeof retrievalQuerySchema>;

export const retrievedChunkSchema = z.object({
  documentId: idSchema,
  documentTitle: z.string(),
  content: z.string(),
  score: z.number(),
});
export type RetrievedChunk = z.infer<typeof retrievedChunkSchema>;
